import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactFlow, { Background, Controls, applyNodeChanges, applyEdgeChanges, ReactFlowProvider, MiniMap, getNodesBounds, useReactFlow } from 'reactflow';
import 'reactflow/dist/style.css';
import { Slider } from '@mui/material';
import dagre from 'dagre';
import CustomNode from './CustomNodeRectangular';
import CustomEdge from './CustomEdge';
import _ from 'lodash';
import { createEdges, createStructure, calculateTimeDifference } from './createEdges';
import { Col } from 'antd';
import { formatDateToDDMMMYYYY } from '../../utils/function';

const nodeWidth = 325;
const nodeHeight = 80;

const nodeTypes = { customNode: CustomNode };
const edgeTypes = { customEdge: CustomEdge };

const getLayoutedElements = (nodes, edges, direction = 'TB') => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: direction,
    nodesep: 250,
    ranksep: 90,
  });

  nodes.forEach(node => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edges.forEach(edge => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  let nullBranchX = null;
  const branchMap = new Map();

  nodes.forEach(node => {
    const nodeWithPosition = dagreGraph.node(node.id);
    node.position = {
      x: nodeWithPosition.x - nodeWidth / 2,
      y: nodeWithPosition.y - nodeHeight / 2,
    };

    const { repo } = node.data;

    if (repo === null) {
      if (nullBranchX === null) {
        nullBranchX = node.position.x;
      }
    } else {
      if (!branchMap.has(repo)) {
        branchMap.set(repo, []);
      }
      branchMap.get(repo).push(node);
    }
  });

  const totalBranches = branchMap.size;
  var offset = 450;

  if (nullBranchX !== null) {
    nodes.forEach(node => {
      const { repo } = node.data;
      if (repo === null) {
        node.position.x = nullBranchX;
      } else {
        const branchIndex = [...branchMap.keys()].indexOf(repo) + (totalBranches > 1);
        node.position.x = nullBranchX + (branchIndex % 2 ? 1 : -1) * (offset * Math.ceil(branchIndex / 2));
      }
    });
  }

  offset = window.innerWidth / 2 - nodes[0].position.x - window.innerWidth / 20;
  nodes.forEach(node => {
    node.position.x += offset;
  });
  return { nodes, edges };
};

const markOverlappingEdges = (nodes, edges) => {
  return edges.map(edge => {
    if (edge.style?.stroke === 'red') {
      return edge;
    }
    const sourceNode = nodes.find(node => node.id === edge.source);
    const targetNode = nodes.find(node => node.id === edge.target);
    
    if (sourceNode && targetNode &&
        sourceNode.data.repo === targetNode.data.repo && 
        sourceNode.position.y > targetNode.position.y) {
      return {
        ...edge,
        type: 'customEdge',
        style: { 
          ...edge.style,
          verticallOffset: 200
        },
      };
    }
    
    return edge;
  });
};

function ItemJourney({ apiUrl, itemId, apiData, counter, setCounter, stateGroups}) {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [unUsedEdges, setUnUsedEdges] = useState([]); // Store unused edges separately
  const [sequence, setSequence] = useState([]); // Sequence of source-target pairs
  const [noDataMessage, setNoDataMessage] = useState('');
  const [translateExtent, setTranslateExtent] = useState([[0, 0], [Infinity, Infinity]]);
  const [horizontalBound, setHorizontalBound] = useState([0, 0])
  const { fitBounds } = useReactFlow(); // Destructure directly from useReactFlow hook
  const [isApiDataChanged, setIsApiDataChanged] = useState(true);

  // Function to fit bounds dynamically based on input
  const fitSelectedNodes = useCallback((x, y, width, height) => {
    fitBounds({ x, y, width, height, duration: 500 });
    // console.log(getViewport());
  }, [fitBounds]);

  function getCategoryByState(state) {
    for (const category in stateGroups) {
      if (stateGroups[category].includes(state)) {
        return category;
      }
    }
    return null;
  }

  useEffect(() => {
    if (apiData) {
      if (_.isEmpty(apiData.lifeCycle)) {
        setNoDataMessage(`No work item found with the given id ${itemId}`);
        setNodes([]);
        setEdges([]);
      } else {
        const visitedRepoStateMap = new Map();

        const nodeList = apiData.lifeCycle.map((item, index) => {
          const repoStateKey = `${item.repo || 'null'}-${item.state}`;

          const isRepeated = visitedRepoStateMap.has(repoStateKey);

          if (!isRepeated) {
            visitedRepoStateMap.set(repoStateKey, true);
          }

          return {
            id: `node-${index}`,
            type: 'customNode',
            data: {
              state: item.state,
              timestamp_utc: [{ timestamp: item.timestamp_utc, nodeId: item.id }],
              repo: item.repo,
              id: item.id,
              apiUrl: apiUrl,
              active: !isRepeated,
              category: getCategoryByState(item.state),
              highlighted: false,
              highlightedTimeStamp: null,
              node_id:`node-${index}`,
            },
            position: { x: 0, y: 0 },
            style: { width: nodeWidth, height: nodeHeight },
          };
        });


        const activeNodeList = nodeList.filter(node => node.data.active === true);
        var edgeList = createStructure(activeNodeList, stateGroups);
        const [additionalEdges, sequenceOfEdges] = createEdges(nodeList); // Get additional edges

        if (additionalEdges.length > 0) {
          const firstEdge = additionalEdges[0];
          firstEdge.animated = true;
          edgeList = [...edgeList, firstEdge];
        }

        setUnUsedEdges(additionalEdges); // Store unused edges separately
        setSequence(sequenceOfEdges); // Set the sequence array

        const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
          activeNodeList,
          edgeList
        );

        setEdges(layoutedEdges);
        setNodes(layoutedNodes);
        setNoDataMessage('');
      }
    }
  }, [itemId, apiData]);

  useEffect(() => {
    if (nodes.length > 0) {
      const bounds = getNodesBounds(nodes);
      const paddingX = 50, paddingY = 50;
      setTranslateExtent([
        [bounds.x - paddingX, bounds.y - paddingY],
        [bounds.width + bounds.x + paddingX, bounds.height + bounds.y + paddingY]
      ]);
      setHorizontalBound([bounds.x - paddingX, bounds.width + bounds.x + paddingX]);

      if(isApiDataChanged){
        setIsApiDataChanged(false);
        if(counter > 0)
          updateEdgesBasedOnCounter();
      }
    }
  }, [nodes]);

  // Function to update edges based on the counter
  const updateEdgesBasedOnCounter = () => {
    if (sequence.length > 0 && counter < sequence.length) {

      setEdges(prevEdges => prevEdges.filter(edge => edge.style.stroke === 'transparent'));

      const newEdges = [];
      const highlightedNodes = new Set();
      const nodeIdToTimeStamp = new Map();

      var xMin = horizontalBound[0];
      var yMin = 1e9;
      var xMax = horizontalBound[1];
      var yMax = 0;

      sequence[counter].forEach(([sourceTimestamp, targetTimestamp]) => {

        var sourceNode = null, targetNode = null;

        nodes.forEach((node) => {
          if (node.data.active) {
            node.data.timestamp_utc.forEach((timeObj) => {
              if (timeObj === sourceTimestamp && !sourceNode) {
                sourceNode = node;
              }
              if (timeObj === targetTimestamp && !targetNode) {
                targetNode = node;
              }
            });
          }
        });

        if (sourceNode && targetNode) {
          const foundEdge = unUsedEdges.find(edge =>
            edge.source === sourceNode.id && edge.target === targetNode.id
          );

          if (foundEdge) {
            foundEdge.label = calculateTimeDifference(sourceTimestamp, targetTimestamp);
            foundEdge.animated = true;
            if(sourceNode.position.y > targetNode.position.y && foundEdge.style.stroke !== 'red')
                    foundEdge.targetHandle = 'bottom'
            newEdges.push(foundEdge);        
          }

          highlightedNodes.add(sourceNode.id);
          highlightedNodes.add(targetNode.id);
          nodeIdToTimeStamp.set(sourceNode.id, sourceTimestamp);
          nodeIdToTimeStamp.set(targetNode.id, targetTimestamp);
          xMax = Math.max(xMax, sourceNode.position.x, targetNode.position.x);
          xMin = Math.min(xMin, sourceNode.position.x, targetNode.position.x);
          yMax = Math.max(yMax, sourceNode.position.y, targetNode.position.y);
          yMin = Math.min(yMin, sourceNode.position.y, targetNode.position.y);
        }
      });

      for (let index = counter-1; index >= 0; index--) {
        sequence[index].forEach(([sourceTimestamp, targetTimestamp]) => {
          var sourceNode = null, targetNode = null;

          nodes.forEach((node) => {
            if (node.data.active) {
              node.data.timestamp_utc.forEach((timeObj) => {
                if (timeObj === sourceTimestamp && !sourceNode) {
                  sourceNode = node;
                }
                if (timeObj === targetTimestamp && !targetNode) {
                  targetNode = node;
                }
              });
            }
          });

          if (sourceNode && targetNode) {
            const foundEdge = unUsedEdges.find(edge =>
              edge.source === sourceNode.id && edge.target === targetNode.id
            );

            if (foundEdge && !newEdges.includes(foundEdge)) {
              foundEdge.label = calculateTimeDifference(sourceTimestamp, targetTimestamp);
              foundEdge.animated = false;
              if(sourceNode.position.y > targetNode.position.y && foundEdge.style.stroke !== 'red')
                    foundEdge.targetHandle = 'bottom'
              newEdges.push(foundEdge);                   
            }
          }
        });
      }
      const width = xMax - xMin;
      const height = yMax - yMin + nodeHeight;

      const combinedEdges = [...newEdges];
      const processedEdges = markOverlappingEdges(nodes, combinedEdges);
      setEdges(processedEdges);

      // Update nodes to include highlighted state and timestamp
      setNodes(prevNodes => {
        const highlightedNodeIds = Array.from(highlightedNodes).filter(nodeId => 
          prevNodes.some(node => node.id === nodeId)
        );
      
        return prevNodes.map(node => {
          const highlighted = highlightedNodeIds.includes(node.id);
          const highlightedTimeStamp = nodeIdToTimeStamp.get(node.id) || null;
      
          return {
            ...node,
            data: {
              ...node.data,
              highlighted,
              highlights: highlighted ? highlightedNodeIds : [],
              highlightedTimeStamp,
            }
          };
        })
      });
      if (width > 0 && height > 0)
        fitSelectedNodes(xMin, yMin, width, height);
    }
  };

  const handleSliderChange = (event, value) => {
    setCounter(value - 1);
  };

  useEffect(() => {
    if (counter >= 0 && sequence.length > 0) {
      updateEdgesBasedOnCounter();
    }
  }, [counter]);

  useEffect(() => {
    if (sequence.length > 0) {
      setCounter(sequence.length - 1);
    }
  }, [sequence.length]);

  const formatValue = (key, value) => {
    switch (key) {
      case 'created_date':
        return formatDateToDDMMMYYYY(value)
      case 'release_date':
        return formatDateToDDMMMYYYY(value);
        case 'lead_time':
        case 'lead_time *':
            return `${value}`;
          default:
            return value;
        }
      };

  const details = useMemo(() => {
    return Object.entries(apiData)
      .filter(([key]) => !['insights', 'lifeCycle', 'title', 'type', 'id','execution_time'].includes(key))
      .map(([key, value]) => {
        if (!value) return null;
        return (
          <li key={key} className="bg-[#f0f8ff] border border-[#e0f7fa] font-medium py-1 shadow-sm transition-all duration-300 ease-in-out overflow-hidden text-ellipsis max-w-full cursor-default mb-2.5 p-2 rounded flex gap-2.5 text-base">
            {key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}: {formatValue(key, value)}
          </li>
        );
      })
      .filter(Boolean);
  }, [apiData]);

  const onNodesChange = (changes) => setNodes((nds) => applyNodeChanges(changes, nds));
  const onEdgesChange = (changes) => setEdges((eds) => applyEdgeChanges(changes, eds));
  const defaultViewport = { x: 0, y: 0, zoom: 0.8 };
  const proOptions = { hideAttribution: true };
  return (
    <div className="h-[85vh] w-full overflow-hidden">
      {noDataMessage ? (
        <div className="no-data-message text-3xl font-semibold mb-2 text-center" style={{ color: "#3f3f3", margin: '15% 0' }}>{noDataMessage}</div>
      ) : (
        <div style={{ height: '80vh', width: '100%' }}>

          {sequence.length > 1 &&
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '8px' }}>
              <Slider
                value={sequence.length <= 1 ? sequence.length : counter + 1||sequence.length}
                onChange={handleSliderChange}
                // valueLabelDisplay="auto"
                step={1}
                min={1}
                max={sequence.length}
                marks
                disabled={!noDataMessage && sequence.length <= 1}
                style={{ width: '80%' }}
              />
            </div>
          }
          {apiData?.title && <Col span={12} className='flex flex-col absolute top-[16%] w-[290px] left-20 '>
            <div className="summary-card">
              <div className=' outline-[transparent] rounded-sm text-base text-[rgba(0,90,158,1)] '>{apiData?.type} {apiData?.id}</div>
              <div className="card-header"  style={{ marginLeft: '-7px' }}>
                <div className="card-title mb-2">{apiData?.title}</div>
                <div className="card-body w-full">
                  <ul>
                    {details}  
                  </ul>
                </div>
              </div>
            </div>
          </Col>}
          <ReactFlow
            translateExtent={translateExtent}
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            nodesDraggable={false}
            style={{ height: '100%', width: '100%',left:'12%' }}
            zoomOnScroll={true}
            panOnScroll={true}
            panOnDrag={true}
            minZoom={0.35}
            maxZoom={0.7}
            defaultViewport={defaultViewport}
            proOptions={proOptions}
          >
            <Background color='#ffffff' />
            <Controls style={{left:'4%'}}/>
            
          </ReactFlow>
          <MiniMap
              nodeStrokeColor={(n) => {
                if (n.style?.background) return n.style.background;
                return '#0041d0';
              }}
              nodeColor={(n) => {
                if (n.style?.background) return n.style.background;
                return '#fff';
              }}
              nodeBorderRadius={2}
              nodeStrokeWidth={2}
              maskColor="rgba(240, 240, 240, 0.7)"
              pannable="true"
              zoomable="true"
              style={{
                backgroundColor: '#333',
                height: 150,
                width: 150,
                position: 'absolute',
                left: 80,
              }}
            />
        </div>
      )}
    </div>
  );
}

export default function ItemJourneyWrapper(props) {
  return (
    <ReactFlowProvider>
      <ItemJourney {...props} />
    </ReactFlowProvider>
  );
}